import jsPDF from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatCurrency } from "./formatCurrency";

// Small helper to mask CPF/CNPJ when available
function formatTaxId(doc) {
  if (!doc) return "-";
  const numbers = String(doc).replace(/\D/g, "");
  if (numbers.length === 11) {
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (numbers.length === 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return doc;
}

function getReceiptNumber(item) {
  return item?.completed_code || format(new Date(), "yyyyMMddHHmmss");
}

function makeFilename(item, index = 0, mode = "receivable") {
  const num = getReceiptNumber(item);
  const id = item?.id ? String(item.id) : String(index);
  const seq = (item?.sequence_number || "").replace(/[^0-9A-Za-z_-]+/g, "");
  const prefix = mode === "payable" ? "RECIBO_PAGAR" : "RECIBO";
  return `${prefix}_${num}_${seq || "SEQ"}_${id}.pdf`;
}

function formatLongDate(dateStr) {
  try {
    const d = new Date(dateStr);
    const day = format(d, "dd");
    const month = format(d, "MMMM", { locale: ptBR });
    const monthCap = month.charAt(0).toUpperCase() + month.slice(1);
    const year = format(d, "yyyy");
    return `${day} de ${monthCap} de ${year}`;
  } catch {
    return dateStr || "-";
  }
}

function formatShortDate(dateStr) {
  try {
    const d = new Date(dateStr);
    return format(d, "dd/MM/yyyy");
  } catch {
    return dateStr || "-";
  }
}

function computeTotals(item) {
  const value = Number(item?.value || 0);
  const discount = Number(item?.discount || 0);
  const fine = Number(item?.fine || 0);
  const interest = Number(item?.daily_interest_rate || 0);
  const honorary = Number(item?.honorary || 0);
  const addition = Number(item?.addition || 0);
  const paymentValue = Number(item?.payment_value || 0);

  // Total geral: valor - desconto + multa + juros + honorários + acréscimos
  const grandTotal = value - discount + fine + interest + honorary + addition;

  return {
    value,
    discount,
    fine,
    interest,
    honorary,
    addition,
    paymentValue,
    grandTotal,
  };
}

function getClient(item) {
  const name = item?.payer?.name || item?.sale?.client?.name || "-";
  const document =
  item?.payer?.doc ||
  item?.payer?.document ||
    item?.sale?.client?.tax_id ||
    item?.sale?.client?.document ||
    item?.client?.document ||
    "";
  return { name, document: formatTaxId(document) };
}

function getSupplier(item) {
  const name = item?.supplier?.name || "-";
  // Prefer supplier.doc when available, then fall back
  const document =
    item?.supplier?.doc ||
    item?.supplier?.tax_id ||
    item?.supplier?.document ||
    "";
  return { name, document: formatTaxId(document) };
}

function getEnterpriseAndLot(item) {
  const enterpriseName = item?.enterprise?.title || item?.enterprise_title || "-";
  const block = item?.block || item?.block_id || "-";
  const land = item?.land || item?.land_id || "-";
  // Prefer a friendly string when block/land is already formatted
  let lotRef = "-";
  if (typeof item?.block === "string" && item?.block?.includes("/")) {
    lotRef = item.block; // e.g., "Q/L" combined
  } else if (land !== "-" || block !== "-") {
    lotRef = `Lote ${land} - Quadra ${block}`;
  }
  return { enterpriseName, lotRef };
}

function getDescription(item) {
  const desc = item?.description?.name || item?.description_name || "Parcela";
  return desc;
}

function getPaymentMethod(item) {
  return item?.payment_method?.name || item?.payment_method_name || "-";
}

function getCompletedBy(item) {
  return item?.completed_by?.name || item?.completed_by_name || "-";
}

function extractLogoBase64(item) {
  return (
    item?.enterprise?.logo_base64 ||
    item?.enterprise?.logo ||
    item?.logo_base64 ||
    item?.logo ||
    null
  );
}

function drawWatermark(doc, base64, options = {}) {
  if (!base64) return;
  const {
    angle = 45,
    opacity = 0.08, // bem sutil
    // When x/y/width/height provided, use them; otherwise center on page using scale
    x: optX,
    y: optY,
    centerX: optCX,
    centerY: optCY,
    width: optW,
    height: optH,
    scale = 0.6, // relative to page min dimension (used only if no explicit size)
  } = options;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const minDim = Math.min(pageWidth, pageHeight);
  const wmWidth = optW ?? (minDim * scale);
  const wmHeight = optH ?? wmWidth; // assume square; jsPDF will scale if not
  // If centerX/centerY provided, compute top-left so rotation occurs around center.
  let x;
  let y;
  if (typeof optCX === "number" && typeof optCY === "number") {
    const theta = (angle * Math.PI) / 180;
    const dx = (wmWidth / 2) * Math.cos(theta) - (wmHeight / 2) * Math.sin(theta);
    const dy = (wmWidth / 2) * Math.sin(theta) + (wmHeight / 2) * Math.cos(theta);
    x = optCX - dx;
    y = optCY - dy;
  } else {
    x = optX ?? ((pageWidth - wmWidth) / 2);
    y = optY ?? ((pageHeight - wmHeight) / 2);
  }

  // Try transparency via GState if available
  let resetGState = null;
  try {
    if (doc.GState) {
      const gs = new doc.GState({ opacity });
      doc.setGState(gs);
      resetGState = () => doc.setGState(new doc.GState({ opacity: 1 }));
    }
  } catch {}

  try {
    // Detect image type
    let format = "PNG";
    if (typeof base64 === "string" && base64.startsWith("data:image/")) {
      const m = base64.match(/^data:image\/(\w+);/i);
      if (m && m[1]) format = m[1].toUpperCase();
    }
    doc.addImage(base64, format, x, y, wmWidth, wmHeight, undefined, "FAST", angle);
  } catch {}

  if (resetGState) {
    try { resetGState(); } catch {}
  }
}

function drawReceipt(doc, item, startY = 40, mode = "receivable") {
  const margin = 40;
  let y = startY;

  const receiptNumber = getReceiptNumber(item);
  const paymentMethod = getPaymentMethod(item);
  const { name: clientName, document } =
    mode === "payable" ? getSupplier(item) : getClient(item);
  const { enterpriseName, lotRef } = getEnterpriseAndLot(item);
  const desc = getDescription(item);
  const seq = item?.sequence_number || "-";
  const dueDate = formatShortDate(item?.due_date);
  const obs = item?.obs || "";
  const completedBy = getCompletedBy(item);
  const longDate = formatLongDate(item?.payment_date || new Date());

  const totals = computeTotals(item);

  doc.setFontSize(12);
  doc.setFont("Helvetica", "bold");
  doc.text(`RECIBO Nº ${receiptNumber}`, margin, y);
  y += 18;

  doc.setFont("Helvetica", "normal");
  doc.text(`(${paymentMethod})`, margin, y);
  y += 18;

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(16);
  doc.text(`${formatCurrency(totals.paymentValue || totals.value)}`, margin, y);
  y += 24;

  doc.setFont("Helvetica", "normal");
  doc.setFontSize(12);
  if (mode === "payable") {
    doc.text(`Pagamos a ${clientName} - CPF/CNPJ: ${document}`, margin, y);
  } else {
    doc.text(`Recebemos de ${clientName} - CPF: ${document}`, margin, y);
  }
  y += 18;

  doc.text(`A importância de ${formatCurrency(totals.paymentValue || totals.value)}`, margin, y);
  y += 18;

  doc.text(`Referente ao ${enterpriseName} - ${lotRef}`, margin, y);
  y += 18;

  // const isEntrada = /entrada/i.test(desc);
  // if (isEntrada) {
  //   doc.text(`( X ) Entrada`, margin, y);
  //   y += 18;
  // }

  doc.text(`Parcela ${desc} ${seq} - Vencimento: ${dueDate}.`, margin, y);
  y += 22;

  const labelCol = margin;
  const valueCol = 420;
  const rowGap = 16;

  const lines = [
    ["Valor Total das Parcelas:", formatCurrency(totals.value)],
    ["Valor Total Recebido:", formatCurrency(totals.paymentValue)],
    ["Desconto Aplicado:", formatCurrency(totals.discount)],
    ["Valor da Parcela:", formatCurrency(totals.value)],
    ["( - ) Descontos", formatCurrency(totals.discount)],
    ["( + ) Multas", formatCurrency(totals.fine)],
    ["( + ) Juros", formatCurrency(totals.interest)],
    ["( + ) Honorarios", formatCurrency(totals.honorary)],
  ];
  if (totals.addition) {
    lines.push(["( + ) Acréscimos", formatCurrency(totals.addition)]);
  }
  lines.push(["Total Geral", formatCurrency(totals.grandTotal)]);

  lines.forEach(([label, value]) => {
    doc.text(label, labelCol, y);
    doc.text(String(value), valueCol, y, { align: "right" });
    y += rowGap;
  });

  y += 10;
  doc.text("Observação:", margin, y);
  y += 16;
  if (obs) {
    const split = doc.splitTextToSize(obs, 520);
    split.forEach((line) => {
      doc.text(line, margin, y);
      y += 14;
    });
  }

  y += 10;
  doc.text("Por se expressão da verdade, firmamos o presente.", margin, y);
  y += 22;

  const city = (item?.enterprise?.city || item?.city || "").toString().toUpperCase();
  const uf = (item?.enterprise?.state || item?.state || "").toString().toUpperCase();
  const locationPrefix = city || uf ? `${city}${uf ? ` - ${uf}` : ""}, ` : "";
  doc.text(`${locationPrefix}${longDate}`, margin, y);
  y += 18;

  doc.text(`Baixado por: ${completedBy}`, margin, y);

  return y; // return last Y used
}

function buildReceiptPdf(item, mode = "receivable", opts = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 60;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = margin;
  const logoBase64 = opts.logoBase64 || extractLogoBase64(item);

  // First copy (1ª via)
  const startY1 = y;
  y = drawReceipt(doc, item, y, mode);
  // Watermark centered within the first receipt content box
  const contentWidth = pageWidth - 2 * margin;
  const wmW1 = contentWidth * 0.55;
  const wmH1 = wmW1;
  // Target center horizontally on page; vertically between start and end of receipt, with a tiny nudge down
  // Aim a bit below the geometric center (60% down the content height)
  let centerY1 = startY1 + (y - startY1) * 0.7;
  const centerX = pageWidth / 2;
  drawWatermark(doc, logoBase64, {
    angle: 45,
    opacity: 0.08,
    centerX,
    centerY: centerY1,
    width: wmW1,
    height: wmH1,
  });

  // Separator
  const sepY = y + 14;
  if (sepY + 10 < pageHeight - margin) {
    doc.setLineWidth(0.5);
    doc.setDrawColor(150);
    doc.setLineDash([3, 3], 0);
    doc.line(margin, sepY, doc.internal.pageSize.getWidth() - margin, sepY);
    doc.setLineDash();
  }

  // Second copy (2ª via)
  let nextStart = sepY + 18;
  if (nextStart > pageHeight - margin - 200) {
    doc.addPage();
    nextStart = margin;
  }
  const startY2 = nextStart;
  const endY2 = drawReceipt(doc, item, nextStart, mode);
  // Watermark centered within the second receipt content box
  const wmW2 = contentWidth * 0.55;
  const wmH2 = wmW2;
  // Also place a little below the middle for the second copy
  let centerY2 = startY2 + (endY2 - startY2) * 0.95;
  // Ensure the second watermark starts below the dotted separator when on the same page
  if (startY2 !== margin) {
    const minCenterY2 = sepY + 16 + wmH2 / 2; // a bit more spacing below the dotted line
    const maxCenterY2 = pageHeight - margin - wmH2 / 2;
    centerY2 = Math.min(Math.max(centerY2, minCenterY2), maxCenterY2);
  }
  drawWatermark(doc, logoBase64, {
    angle: 45,
    opacity: 0.08,
    centerX: pageWidth / 2,
    centerY: centerY2,
    width: wmW2,
    height: wmH2,
  });

  return doc;
}

export async function generateReceiptsPdfs(selectedItems = [], opts = {}) {
  const paid = (selectedItems || []).filter((it) => Number(it.status) === 1);
  if (paid.length === 0) {
    throw new Error("Selecione ao menos uma parcela paga.");
  }

  if (paid.length === 1) {
    const item = paid[0];
  const doc = buildReceiptPdf(item, "receivable", opts);
    const filename = makeFilename(item, 0, "receivable");
    doc.save(filename);
    return { type: "single", filename };
  }

  // Multiple: generate ZIP
  const zip = new JSZip();
  for (let i = 0; i < paid.length; i++) {
    const item = paid[i];
  const doc = buildReceiptPdf(item, "receivable", opts);
    const arrayBuffer = doc.output("arraybuffer");
    const filename = makeFilename(item, i, "receivable");
    zip.file(filename, arrayBuffer);
  }
  const content = await zip.generateAsync({ type: "blob" });
  const zipName = `recibos_${format(new Date(), "yyyyMMdd_HHmmss")}.zip`;
  saveAs(content, zipName);
  return { type: "zip", filename: zipName };
}

export async function generatePayablesReceiptsPdfs(selectedItems = [], opts = {}) {
  const paid = (selectedItems || []).filter((it) => Number(it.status) === 1);
  if (paid.length === 0) {
    throw new Error("Selecione ao menos uma parcela paga.");
  }

  if (paid.length === 1) {
    const item = paid[0];
  const doc = buildReceiptPdf(item, "payable", opts);
    const filename = makeFilename(item, 0, "payable");
    doc.save(filename);
    return { type: "single", filename };
  }

  const zip = new JSZip();
  for (let i = 0; i < paid.length; i++) {
    const item = paid[i];
  const doc = buildReceiptPdf(item, "payable", opts);
    const arrayBuffer = doc.output("arraybuffer");
    const filename = makeFilename(item, i, "payable");
    zip.file(filename, arrayBuffer);
  }
  const content = await zip.generateAsync({ type: "blob" });
  const zipName = `recibos_pagar_${format(new Date(), "yyyyMMdd_HHmmss")}.zip`;
  saveAs(content, zipName);
  return { type: "zip", filename: zipName };
}
